import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Handshake, Plus, X, Trash2, Edit3, Globe, Building2,
  Upload, AlertCircle, ChevronDown, Check,
} from "lucide-react";
import { Link as LinkIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_LOGO_BYTES = 500 * 1024; // 500KB

const emptyForm = { name: "", logo_url: "", link_url: "" };

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const AdminPartnersSection = ({ admin, clubs: clubsProp = [] }) => {
  const [clubs, setClubs] = useState(clubsProp);
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [overrideLoading, setOverrideLoading] = useState(false);

  const [globalPartners, setGlobalPartners] = useState([]);
  const [clubPartners, setClubPartners] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState("");
  const [maxPerScope, setMaxPerScope] = useState(5);

  const [loadingGlobal, setLoadingGlobal] = useState(false);
  const [loadingClub, setLoadingClub] = useState(false);
  const [loadingClubs, setLoadingClubs] = useState(false);

  const [formScope, setFormScope] = useState(null); // "global" | "club" | null
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Self-sufficient clubs fetch — don't rely on parent's state
  const fetchClubs = useCallback(async () => {
    setLoadingClubs(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/clubs?admin_id=${admin.id}&limit=500&sort_by=name&sort_order=asc`
      );
      setClubs(res.data?.items || []);
    } catch {
      toast.error("Failed to load clubs");
    } finally {
      setLoadingClubs(false);
    }
  }, [admin.id]);

  const fetchOverride = useCallback(async () => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/settings/partners-override?admin_id=${admin.id}`
      );
      setOverrideEnabled(!!res.data.enabled);
    } catch {
      /* noop */
    }
  }, [admin.id]);

  const fetchGlobal = useCallback(async () => {
    setLoadingGlobal(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/partners?admin_id=${admin.id}&scope=global`
      );
      setGlobalPartners(res.data.items || []);
      setMaxPerScope(res.data.max || 5);
    } catch (e) {
      toast.error("Failed to load global partners");
    } finally {
      setLoadingGlobal(false);
    }
  }, [admin.id]);

  const fetchClub = useCallback(
    async (clubId) => {
      if (!clubId) {
        setClubPartners([]);
        return;
      }
      setLoadingClub(true);
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/admin/partners?admin_id=${admin.id}&scope=club&club_id=${clubId}`
        );
        setClubPartners(res.data.items || []);
      } catch (e) {
        toast.error("Failed to load club partners");
      } finally {
        setLoadingClub(false);
      }
    },
    [admin.id]
  );

  useEffect(() => {
    fetchOverride();
    fetchGlobal();
    fetchClubs();
  }, [fetchOverride, fetchGlobal, fetchClubs]);

  useEffect(() => {
    if (selectedClubId) fetchClub(selectedClubId);
  }, [selectedClubId, fetchClub]);

  const toggleOverride = async () => {
    setOverrideLoading(true);
    try {
      const next = !overrideEnabled;
      await axios.patch(
        `${BACKEND_URL}/api/admin/settings/partners-override?admin_id=${admin.id}`,
        { enabled: next }
      );
      setOverrideEnabled(next);
      toast.success(
        next
          ? "Global partners now override club partners for all users"
          : "Each club shows its own partners again"
      );
    } catch (e) {
      toast.error("Failed to update override setting");
    } finally {
      setOverrideLoading(false);
    }
  };

  const openCreate = (scope) => {
    setFormScope(scope);
    setEditingId(null);
    setForm(emptyForm);
  };

  const openEdit = (partner) => {
    setFormScope(partner.scope);
    setEditingId(partner.id);
    setForm({
      name: partner.name || "",
      logo_url: partner.logo_url || "",
      link_url: partner.link_url || "",
    });
  };

  const closeForm = () => {
    setFormScope(null);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Logo file is too large (max 500KB)");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setForm((f) => ({ ...f, logo_url: dataUrl }));
    } catch {
      toast.error("Could not read file");
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!form.logo_url) {
      toast.error("Logo is required (URL or upload)");
      return;
    }
    if (formScope === "club" && !selectedClubId) {
      toast.error("Select a club first");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(
          `${BACKEND_URL}/api/admin/partners/${editingId}?admin_id=${admin.id}`,
          {
            name: form.name.trim(),
            logo_url: form.logo_url,
            link_url: form.link_url.trim() || null,
          }
        );
        toast.success("Partner updated");
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/partners?admin_id=${admin.id}`, {
          name: form.name.trim(),
          logo_url: form.logo_url,
          link_url: form.link_url.trim() || null,
          scope: formScope,
          club_id: formScope === "club" ? selectedClubId : null,
        });
        toast.success("Partner added");
      }
      closeForm();
      if (formScope === "global") fetchGlobal();
      else fetchClub(selectedClubId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save partner");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (partner) => {
    if (!window.confirm(`Delete "${partner.name}"?`)) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/partners/${partner.id}?admin_id=${admin.id}`
      );
      toast.success("Partner deleted");
      if (partner.scope === "global") fetchGlobal();
      else fetchClub(selectedClubId);
    } catch {
      toast.error("Failed to delete partner");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-partners-section">
      {/* Override toggle */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-4 h-4 text-blue-300" />
              <h3 className="text-base font-semibold">Global override</h3>
            </div>
            <p className="text-xs text-white/60 max-w-xl">
              When enabled, <strong>every user</strong> in the app sees the global partners
              below — overriding any club-specific partners. Turn off to let each club
              show its own partners (with global as fallback).
            </p>
          </div>
          <button
            onClick={toggleOverride}
            disabled={overrideLoading}
            data-testid="admin-partners-override-toggle"
            aria-pressed={overrideEnabled}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${
              overrideEnabled ? "bg-gradient-to-r from-blue-500 to-cyan-500" : "bg-white/15"
            } disabled:opacity-50`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                overrideEnabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Global partners list */}
      <PartnersList
        title="Global partners"
        subtitle={`Visible to users without a club ${overrideEnabled ? "and ALL users (override on)" : "and as fallback when a club has none"}`}
        icon={Globe}
        partners={globalPartners}
        loading={loadingGlobal}
        max={maxPerScope}
        onAdd={() => openCreate("global")}
        onEdit={openEdit}
        onDelete={handleDelete}
        testIdPrefix="global"
      />

      {/* Club partners selector + list */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-orange-300" />
          <h3 className="text-base font-semibold">Per-club partners</h3>
        </div>
        <div>
          <label className="block text-xs text-white/60 mb-1.5">
            Select a club {loadingClubs ? "(loading...)" : `(${clubs.length} available)`}
          </label>
          <ClubDropdown
            clubs={clubs}
            value={selectedClubId}
            onChange={setSelectedClubId}
            disabled={loadingClubs}
          />
        </div>

        {selectedClubId ? (
          <PartnersList
            title=""
            subtitle={`These partners appear only for members of this club${
              overrideEnabled ? " — currently hidden because global override is ON" : ""
            }.`}
            icon={null}
            partners={clubPartners}
            loading={loadingClub}
            max={maxPerScope}
            onAdd={() => openCreate("club")}
            onEdit={openEdit}
            onDelete={handleDelete}
            testIdPrefix="club"
            embedded
          />
        ) : (
          <div className="text-xs text-white/40 py-4 text-center border border-dashed border-white/10 rounded-xl">
            Choose a club above to manage its partners
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {formScope && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={closeForm}
          data-testid="admin-partner-modal"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-5 md:p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {editingId ? "Edit partner" : "Add partner"}
                </h3>
                <p className="text-xs text-white/50 mt-0.5">
                  {formScope === "global" ? "Global (all users)" : `Club: ${clubs.find((c) => c.id === selectedClubId)?.name || ""}`}
                </p>
              </div>
              <button onClick={closeForm} className="text-white/40 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-white/60 mb-1.5">Partner name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Nike"
                  maxLength={80}
                  className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm focus:border-blue-500/50 focus:outline-none"
                  data-testid="admin-partner-name-input"
                />
              </div>

              <div>
                <label className="block text-xs text-white/60 mb-1.5">
                  <LinkIcon className="w-3 h-3 inline mr-1" /> Logo URL
                </label>
                <input
                  type="url"
                  value={form.logo_url?.startsWith("data:") ? "" : form.logo_url}
                  onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm focus:border-blue-500/50 focus:outline-none"
                  data-testid="admin-partner-logo-url-input"
                />
                <div className="flex items-center gap-2 my-2">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-xs text-white/40">or</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
                <label
                  className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 cursor-pointer text-sm"
                  data-testid="admin-partner-logo-upload-label"
                >
                  <Upload className="w-4 h-4" /> Upload logo (max 500KB)
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFile}
                    className="hidden"
                    data-testid="admin-partner-logo-upload-input"
                  />
                </label>
                {form.logo_url && (
                  <div className="mt-3 p-3 rounded-xl bg-white/5 border border-white/10 flex items-center gap-3">
                    <div className="w-14 h-14 rounded-lg bg-white/5 flex items-center justify-center overflow-hidden flex-shrink-0">
                      <img
                        src={form.logo_url}
                        alt="preview"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="text-xs text-white/60 truncate flex-1">
                      Logo preview
                    </div>
                    <button
                      onClick={() => setForm({ ...form, logo_url: "" })}
                      className="text-white/40 hover:text-red-400"
                      title="Remove logo"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs text-white/60 mb-1.5">Link URL (optional)</label>
                <input
                  type="url"
                  value={form.link_url}
                  onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                  placeholder="https://partner.com"
                  className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm focus:border-blue-500/50 focus:outline-none"
                  data-testid="admin-partner-link-input"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-5">
              <button
                onClick={closeForm}
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-sm text-white/80 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name || !form.logo_url}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold text-sm hover:opacity-90 disabled:opacity-50"
                data-testid="admin-partner-save-button"
              >
                {saving ? "Saving..." : editingId ? "Save changes" : "Add partner"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PartnersList = ({
  title,
  subtitle,
  icon: Icon,
  partners,
  loading,
  max,
  onAdd,
  onEdit,
  onDelete,
  testIdPrefix,
  embedded = false,
}) => {
  const canAdd = partners.length < max;
  const Wrapper = embedded ? "div" : "div";
  return (
    <Wrapper className={embedded ? "" : "rounded-2xl border border-white/10 bg-white/5 p-5"}>
      {title && (
        <div className="flex items-center gap-2 mb-1">
          {Icon && <Icon className="w-4 h-4 text-blue-300" />}
          <h3 className="text-base font-semibold">{title}</h3>
        </div>
      )}
      {subtitle && <p className="text-xs text-white/60 mb-4">{subtitle}</p>}

      <div className="space-y-2">
        {loading ? (
          <div className="text-xs text-white/40 py-4 text-center">Loading...</div>
        ) : partners.length === 0 ? (
          <div className="text-xs text-white/40 py-4 text-center border border-dashed border-white/10 rounded-xl">
            No partners yet
          </div>
        ) : (
          partners.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5"
              data-testid={`admin-partner-${testIdPrefix}-row-${p.id}`}
            >
              <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center overflow-hidden flex-shrink-0">
                <img src={p.logo_url} alt={p.name} className="max-w-full max-h-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{p.name}</p>
                {p.link_url && (
                  <p className="text-xs text-white/40 truncate">{p.link_url}</p>
                )}
              </div>
              <button
                onClick={() => onEdit(p)}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60"
                title="Edit"
                data-testid={`admin-partner-edit-${p.id}`}
              >
                <Edit3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(p)}
                className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400"
                title="Delete"
                data-testid={`admin-partner-delete-${p.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-xs text-white/40">
          {partners.length} / {max}
        </p>
        <button
          onClick={onAdd}
          disabled={!canAdd}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-blue-500/80 to-cyan-500/80 text-white text-sm font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`admin-partner-add-${testIdPrefix}`}
        >
          <Plus className="w-4 h-4" /> Add partner
        </button>
      </div>

      {!canAdd && (
        <div className="mt-2 flex items-center gap-2 text-xs text-yellow-300/80">
          <AlertCircle className="w-3 h-3" /> Max {max} reached
        </div>
      )}
    </Wrapper>
  );
};

const ClubDropdown = ({ clubs, value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = clubs.find((c) => c.id === value);

  return (
    <div className="relative" ref={ref} data-testid="admin-partners-club-dropdown">
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled || clubs.length === 0}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        data-testid="admin-partners-club-select"
      >
        <span className={selected ? "text-white" : "text-white/50"}>
          {selected
            ? selected.name
            : clubs.length === 0
            ? "— No clubs yet —"
            : "— Choose a club —"}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-white/40 transition-transform flex-shrink-0 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && clubs.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-xl border border-white/10 bg-zinc-950 shadow-xl">
          {clubs.map((club) => (
            <button
              key={club.id}
              type="button"
              onClick={() => {
                onChange(club.id);
                setOpen(false);
              }}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm text-left hover:bg-white/10 transition-colors ${
                value === club.id ? "bg-white/5 text-white" : "text-white/85"
              }`}
              data-testid={`admin-partners-club-option-${club.id}`}
            >
              <span className="truncate">{club.name}</span>
              {value === club.id && (
                <Check className="w-4 h-4 text-orange-300 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPartnersSection;